import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'

import { useGetOrderQuery } from '@/app/services/orders'
import { selectCustomerId } from '@/app/slices/customerSlice'
import { selectGuestUser, selectIsGuestLoggedIn, setOpenGuestLoginDialog } from '@/app/slices/guestUserSlice'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { Paths } from '@/common/utils'
import { ORDER_PAYMENT_STATUS, ORDER_STATUS_ARRAY, ORDER_USER_TYPE } from '@/common/utils/Constants/Constants'

export const useOrderNavigation = (page = 'order') => {
    const { orderId } = useParams()
    const dispatch = useDispatch()
    const { isAuthenticated, login } = useKindeAuth()

    const currentCustomerId = useSelector(selectCustomerId)
    const isGuestLoggedIn = useSelector(selectIsGuestLoggedIn)
    const guestUser = useSelector(selectGuestUser)
    const organisationId = useSelector(selectOrganisationId)
    const organisation = useSelector(selectOrganisation)
    const storeSettings = useSelector(selectOrganisation)

    const {
        data: order,
        error,
        isError,
        isLoading,
    } = useGetOrderQuery(
        {
            organisationId,
            orderId,
        },
        {
            skip: !organisationId || !orderId,
        }
    )

    const orderStatus = useMemo(
        () => ({
            isEditable: [ORDER_STATUS_ARRAY.NotCalculated, ORDER_STATUS_ARRAY.Calculated].includes(order?.status),
            isDraft: order?.status === ORDER_STATUS_ARRAY.Draft,
            isIssued: order?.status === ORDER_STATUS_ARRAY.Issued,
            isOrdered: [
                ORDER_STATUS_ARRAY.PendingOrderConfirmation,
                ORDER_STATUS_ARRAY.Ordered,
                ORDER_STATUS_ARRAY.Invoiced,
            ].includes(order?.status),
            isPaid: [ORDER_PAYMENT_STATUS.GatewayPaid, ORDER_PAYMENT_STATUS.ManualPaid].includes(order?.paymentStatus),
        }),
        [order?.status, order?.paymentStatus]
    )

    const handleCustomConfirmation = useCallback(() => {
        if (organisation?.webStoreCustomOrderConfirmationUrl) {
            window.location.assign(organisation.webStoreCustomOrderConfirmationUrl)
            return true
        }
        return false
    }, [organisation?.webStoreCustomOrderConfirmationUrl])

    // Early returns for loading and error states
    if (isLoading) return null
    if (!storeSettings?.webStoreIsPublic && !isAuthenticated) return <Navigate to={Paths.HOME_PATHNAME} />
    if (isError) {
        if (error?.status >= 500) return <Navigate to={Paths.ERROR_PATHNAME} />
        return <Navigate to={Paths.NOT_FOUND} />
    }
    if (!order || Object.keys(order).length === 0) return <Navigate to={Paths.NOT_FOUND} />

    // Authentication checks
    if (order?.quoteUserType === ORDER_USER_TYPE.Internal) {
        return <Navigate to={Paths.HOME_PATHNAME} />
    }

    if (order?.customerContactId) {
        console.log('3')
        // Check if authenticated user is accessing the correct customer order
        if (
            isAuthenticated &&
            currentCustomerId !== order?.customerContact?.customerId &&
            currentCustomerId !== order?.customerId
        ) {
            console.log('4')
            return <Navigate to={Paths.NOT_FOUND} />
        }

        // Check if guest user is accessing the correct customer order
        if (
            isGuestLoggedIn &&
            guestUser?.customerId !== order?.customerContact?.customerId &&
            guestUser?.customerId !== order?.customerId
        ) {
            console.log('5', order, guestUser, guestUser?.customerId, order?.customerContact?.customerId)
            return <Navigate to={Paths.NOT_FOUND} />
        }

        // Handle unauthenticated users
        if (!isAuthenticated && !isGuestLoggedIn) {
            console.log('6')
            // If user has a Kinde account, redirect to login
            if (order?.customerContact?.isKindeUser) {
                console.log('7')
                login({
                    org_code: storeSettings?.kindeOrganisationId,
                    app_state: { redirectTo: window.location.href },
                })
                return true
            }

            // For non-Kinde users, open guest login dialog
            dispatch(setOpenGuestLoginDialog(page))
        }
    }

    // Page-specific navigation logic
    const { isDraft, isEditable, isIssued, isOrdered, isPaid } = orderStatus

    if (page === 'order') {
        if (isDraft) return <Navigate to={Paths.ORDER_REVIEW_PATHNAME.replace(':orderId', order.id)} />
        if (isIssued) return <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />
        if (isOrdered && handleCustomConfirmation()) return null
        if (isOrdered) return <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />
    }

    if (page === 'review') {
        if (isEditable) return <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} />
        if (isIssued) return <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />
        if (isOrdered && handleCustomConfirmation()) return null
        if (isOrdered) return <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />
    }

    if (page === 'checkout') {
        if (isEditable) return <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} />
        if (isOrdered && handleCustomConfirmation()) return null
        if (isOrdered) return <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />
    }

    if (page === 'confirmation') {
        if (!isOrdered) {
            if (isEditable) return <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} />
            if (isDraft) return <Navigate to={Paths.ORDER_REVIEW_PATHNAME.replace(':orderId', order.id)} />
            if (isIssued) return <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />
        }
        if (isPaid) return <Navigate to={Paths.ORDER_PAYMENT_SUCCESS.replace(':orderId', order.id)} />
        if (handleCustomConfirmation()) return null
    }

    if (page === 'paymentSuccess') {
        if (!isOrdered) {
            if (isEditable) return <Navigate to={Paths.ORDER_PATHNAME.replace(':orderId', order.id)} />
            if (isDraft) return <Navigate to={Paths.ORDER_REVIEW_PATHNAME.replace(':orderId', order.id)} />
            if (isIssued) return <Navigate to={Paths.ORDER_CHECKOUT_PATHNAME.replace(':orderId', order.id)} />
        }
        if (!isPaid) return <Navigate to={Paths.ORDER_CONFIRMATION_PATHNAME.replace(':orderId', order.id)} />
    }

    return null
}
